// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import { Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { EducBox, ChartBox, ResultCard, InputRow, fmt, baseChartOpts } from './shared';

const calcPMT = (fv, pv, r, n) => {
  if (r === 0) return n > 0 ? Math.max(0, (fv - pv) / n) : 0;
  const factor = Math.pow(1 + r, n);
  return Math.max(0, (fv - pv * factor) * r / (factor - 1));
};

const ObiettivoFinanziario = () => {
  const [nome, setNome] = useState('');
  const [importo, setImporto] = useState('10000');
  const [mesi, setMesi] = useState(36);
  const [giaSalvato, setGiaSalvato] = useState('0');
  const [tasso, setTasso] = useState('3');

  const F = parseFloat(importo) || 0;
  const PV = parseFloat(giaSalvato) || 0;
  const rAnnuo = parseFloat(tasso) || 0;
  const rMensile = rAnnuo / 100 / 12;
  const n = Math.max(1, mesi);

  const pmt = useMemo(() => calcPMT(F, PV, rMensile, n), [F, PV, rMensile, n]);
  const totaleVersato = PV + pmt * n;
  const interessiGuadagnati = Math.max(0, F - totaleVersato);
  const oggi = new Date();
  const dataRagg = new Date(oggi.getFullYear(), oggi.getMonth() + n, 1);
  const dataStr = dataRagg.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });

  const labels = [];
  const accData = [];
  const targetData = [];
  for (let i = 0; i <= n; i += Math.max(1, Math.floor(n / 12))) {
    const factor = rMensile === 0 ? 1 : Math.pow(1 + rMensile, i);
    const acc = PV * factor + (rMensile === 0 ? pmt * i : pmt * (factor - 1) / rMensile);
    labels.push(`Mese ${i}`);
    accData.push(Math.round(Math.min(acc, F)));
    targetData.push(Math.round(F));
  }

  const anni = Math.floor(mesi / 12);
  const mesiRest = mesi % 12;
  const durataTesto = anni > 0 ? `${anni} ann${anni === 1 ? 'o' : 'i'}${mesiRest > 0 ? ` e ${mesiRest} mesi` : ''}` : `${mesi} mesi`;

  const suggerimento = mesi < 12
    ? 'Per obiettivi brevi usa un conto deposito libero — sicuro e accessibile in qualsiasi momento.'
    : mesi <= 36
    ? 'Considera un conto deposito vincolato per rendimenti migliori (2-4%/anno).'
    : 'Per obiettivi oltre 3 anni considera un ETF obbligazionario per battere l\'inflazione.';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <EducBox text="Che si tratti di comprare un'auto, fare un viaggio o costruire l'anticipo per la casa — ogni obiettivo finanziario ha un piano di risparmio ottimale. Inserisci il tuo obiettivo e scopri come raggiungerlo." />

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-[#888780] text-sm block mb-1">Nome obiettivo (opzionale)</label>
          <input type="text" value={nome} onChange={e => setNome(e.target.value)} placeholder="Es. Anticipo casa, Viaggio Giappone..." className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm" data-testid="obiettivo-nome" />
        </div>
        <InputRow label="Importo obiettivo (€)" value={importo} onChange={setImporto} prefix="€" />
        <InputRow label="Già risparmiato (€)" value={giaSalvato} onChange={setGiaSalvato} prefix="€" />
        <InputRow label="Rendimento atteso (%/anno)" value={tasso} onChange={setTasso} suffix="%" tooltip="Per obiettivi brevi (<2 anni) usa 2-3% (conto deposito). Per obiettivi lunghi (>5 anni) puoi usare 5-7% (ETF)." />
      </div>

      <div className="mb-6">
        <label className="text-[#888780] text-sm block mb-2">
          Orizzonte temporale: <strong className="text-[#E8E8E4]">{durataTesto}</strong>
        </label>
        <input type="range" min={6} max={120} step={1} value={mesi} onChange={e => setMesi(parseInt(e.target.value))}
          className="w-full accent-[#C9A84C] cursor-pointer" data-testid="obiettivo-slider" />
        <div className="flex justify-between text-xs text-[#888780] mt-1">
          <span>6 mesi</span><span>5 anni</span><span>10 anni</span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4" data-testid="obiettivo-results">
        <div>
          <div className="p-4 bg-[#252523] rounded-xl border border-white/[0.06]">
            <p className="text-xs text-[#888780] mb-1">Devi risparmiare</p>
            <p className="text-2xl font-bold text-[#C9A84C]">€{fmt(pmt)}/mese</p>
          </div>
        </div>
        <ResultCard label="Totale che verserai" value={`€${fmt(totaleVersato)}`} />
        <ResultCard label="Interessi guadagnati" value={`€${fmt(interessiGuadagnati)}`} green />
        <ResultCard label="Data raggiungimento" value={dataStr} sub={`in ${durataTesto}`} gold />
      </div>

      <ChartBox title="Accumulo verso l'obiettivo">
        <Line
          data={{
            labels,
            datasets: [
              { label: 'Risparmio accumulato', data: accData, borderColor: '#3D7A52', backgroundColor: 'rgba(61,122,82,0.1)', fill: true, tension: 0.4, pointRadius: 0 },
              { label: nome || 'Obiettivo', data: targetData, borderColor: '#C9A84C', borderDash: [6, 3], fill: false, pointRadius: 0 },
            ],
          }}
          options={{ ...baseChartOpts(), plugins: { ...baseChartOpts().plugins, legend: { display: true, labels: { color: '#888780' } } } }}
        />
      </ChartBox>

      <div className="mt-4 p-4 rounded-xl text-sm" style={{ background: 'rgba(91,141,184,0.08)', border: '1px solid rgba(91,141,184,0.25)' }}>
        <p className="text-[#5B8DB8] font-medium mb-1">Suggerimento</p>
        <p className="text-[#888780]">{suggerimento}</p>
      </div>

      {pmt > 0 && (
        <div className="mt-4 flex justify-end">
          <button
            data-testid="share-obiettivo-btn"
            onClick={() => {
              const text = `Sto risparmiando €${fmt(pmt)}/mese per raggiungere €${fmt(F)}${nome ? ` (${nome})` : ''} entro ${durataTesto}! Calcola anche tu il tuo obiettivo su SAPIENS 🎯 finsapiens.it`;
              if (navigator.share) navigator.share({ text });
              else { navigator.clipboard.writeText(text); toast.success('Testo copiato!'); }
            }}
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-xl text-[#888780] hover:text-[#E8E8E4] transition-colors"
            style={{ border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <Share2 className="w-3.5 h-3.5" />Condividi risultato
          </button>
        </div>
      )}
</motion.div>
  );
};

export default ObiettivoFinanziario;
